/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ReactElement } from 'react';
import * as React from 'react';
import { memo } from 'react';

import classNames from 'classnames';

import type { ButtonProps } from '@coursera/cds-core';
import { Button } from '@coursera/cds-core';
import type { EoiInteraction } from '@coursera/event-pulse-types/index';

import type { PersonalizedMessageName } from 'bundles/degree-description/utils/personalizedMessageContent';
import { ACTION_TYPES, INTERACTION_EVENTS_MAP } from 'bundles/expression-of-interest/constants';
import type { ActionConfigProps, ExpressionOfInterestTextProps } from 'bundles/expression-of-interest/types';
import { useEoiEventing } from 'bundles/expression-of-interest/utils/eventingUtils';
import { appendUtmParametersToApplicationLink } from 'bundles/expression-of-interest/utils/util';
import TrackedButton from 'bundles/page/components/TrackedButton';
import type { TrackingProps } from 'bundles/page/components/TrackedLink2';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/expression-of-interest';

const styles = {
  mb: css`
    margin-bottom: var(--cds-spacing-100);
  `,
};

type Props = {
  actionConfig: ActionConfigProps;
  trackingProductId: string;
  expressionOfInterestText?: ExpressionOfInterestTextProps | null;
  trackingNamePrefix?: string;
  personalizedMessageName?: PersonalizedMessageName;
  trackingIndex?: number;
  setEoiTrackingIndex?: React.Dispatch<React.SetStateAction<number>>;
  isInEOI?: boolean;
};

const EoiButton = ({
  actionConfig,
  trackingProductId,
  expressionOfInterestText,
  trackingNamePrefix,
  personalizedMessageName,
  trackingIndex,
  setEoiTrackingIndex,
  isInEOI,
}: Props): ReactElement | null => {
  const { trackClickButton, trackInteractEoi } = useEoiEventing(trackingProductId);
  const isLink = actionConfig?.buttonOptions?.tag === 'a';

  const handleClick = (v3EventKey: EoiInteraction | 'degree_apply') => {
    const { actionType, buttonOptions, doAction } = actionConfig;

    if (actionType === ACTION_TYPES.requestInfo && doAction) {
      doAction();
    } else {
      buttonOptions?.onClick?.(actionType);
    }

    if (setEoiTrackingIndex) {
      setEoiTrackingIndex(trackingIndex || 1);
    }

    const href = actionConfig.buttonOptions?.htmlAttributes?.href;

    if (v3EventKey === 'degree_apply') {
      const buttonName = isInEOI ? 'degree_apply_from_eoi' : 'degree_apply';
      trackClickButton(buttonName, 'external', href, trackingIndex);
    } else {
      trackInteractEoi(v3EventKey, null, trackingIndex, personalizedMessageName);
    }
  };

  const getTrackingName = (defaultTrackingName: string, overridePrefix?: string) => {
    if (overridePrefix) {
      return `${overridePrefix}_${defaultTrackingName}`;
    } else {
      return actionConfig?.buttonOptions?.trackingName ?? defaultTrackingName;
    }
  };

  switch (actionConfig.actionType) {
    // This action type is deprecated.
    // It was introduced as a temporary mesaure to bridge the gap between EOI V1 and V2
    case ACTION_TYPES.requestInfo: {
      const { v2TrackingName, v3EventKey } = INTERACTION_EVENTS_MAP.requestInfo;
      return (
        <Button
          variant={(actionConfig?.buttonOptions?.type as ButtonProps['variant']) ?? 'secondary'}
          trackingName={getTrackingName(v2TrackingName, trackingNamePrefix)}
          data={actionConfig?.buttonOptions?.trackingData ?? {}}
          onClick={() => handleClick(v3EventKey)}
          component={<T extends TrackingProps>(props: T) => <TrackedLink2 {...props} href="#requestinfo" />}
          fullWidth
          css={styles.mb}
        >
          {actionConfig?.buttonOptions?.label || expressionOfInterestText?.emailMeButtonText || _t('Email me info')}
        </Button>
      );
    }
    case ACTION_TYPES.apply: {
      const { v2TrackingName } = INTERACTION_EVENTS_MAP.apply;
      return (
        <Button
          disabled={actionConfig?.buttonOptions?.disabled ?? false}
          // TODO: Update type here to accept the actual button type
          variant={(actionConfig?.buttonOptions?.type as ButtonProps['variant']) ?? 'primary'}
          trackingName={getTrackingName(v2TrackingName, trackingNamePrefix)}
          data={actionConfig?.buttonOptions?.trackingData ?? {}}
          className={classNames('rc-ApplyButton', actionConfig.buttonOptions?.rootClassName)}
          css={styles.mb}
          onClick={() => handleClick('degree_apply')}
          component={<T extends TrackingProps>(props: T) => {
            if (isLink) {
              let htmlAttributes = actionConfig.buttonOptions?.htmlAttributes;
              if (htmlAttributes?.href) {
                const href = appendUtmParametersToApplicationLink(htmlAttributes.href);
                htmlAttributes = { ...htmlAttributes, href };
              }
              return <TrackedLink2 {...props} {...htmlAttributes} />;
            } else {
              return <TrackedButton {...props} />;
            }
          }}
          fullWidth
          data-e2e="eoi-apply"
        >
          {actionConfig?.buttonOptions?.label || expressionOfInterestText?.applyButtonText || _t('Apply')}
        </Button>
      );
    }
    case ACTION_TYPES.emailMeInfo: {
      const { v2TrackingName, v3EventKey } = INTERACTION_EVENTS_MAP.emailMeInfo;
      return (
        <Button
          disabled={actionConfig?.buttonOptions?.disabled ?? false}
          // TODO: Update type here to accept the actual button type
          variant={(actionConfig?.buttonOptions?.type as ButtonProps['variant']) ?? 'secondary'}
          trackingName={getTrackingName(v2TrackingName, trackingNamePrefix)}
          data={actionConfig?.buttonOptions?.trackingData ?? {}}
          className={classNames('rc-EmailMeButton', actionConfig.buttonOptions?.rootClassName)}
          css={styles.mb}
          onClick={() => handleClick(v3EventKey)}
          component={<T extends TrackingProps>(props: T) =>
            isLink ? (
              <TrackedLink2 {...props} {...actionConfig.buttonOptions?.htmlAttributes} />
            ) : (
              <TrackedButton {...props} />
            )
          }
          fullWidth
        >
          {actionConfig?.buttonOptions?.label || expressionOfInterestText?.emailMeButtonText || _t('Email me info')}
        </Button>
      );
    }
    case ACTION_TYPES.qualifyingQuestions: {
      const { v3EventKey } = INTERACTION_EVENTS_MAP.qualifyingQuestions;
      return (
        <Button
          disabled={actionConfig?.buttonOptions?.disabled ?? false}
          // TODO: Update type here to accept the actual button type
          variant={(actionConfig?.buttonOptions?.type as ButtonProps['variant']) ?? 'secondary'}
          data={actionConfig?.buttonOptions?.trackingData ?? {}}
          className={classNames('rc-EmailMeButton', actionConfig.buttonOptions?.rootClassName)}
          css={styles.mb}
          onClick={() => handleClick(v3EventKey)}
          component={<T extends TrackingProps>(props: T) =>
            isLink ? (
              <TrackedLink2 {...props} {...actionConfig.buttonOptions?.htmlAttributes} />
            ) : (
              <TrackedButton {...props} />
            )
          }
          fullWidth
        >
          {actionConfig?.buttonOptions?.label || _t('Get next steps')}
        </Button>
      );
    }

    case ACTION_TYPES.nextSteps: {
      const { v3EventKey } = INTERACTION_EVENTS_MAP.nextSteps;
      return (
        <Button
          disabled={actionConfig?.buttonOptions?.disabled ?? false}
          // TODO: Update type here to accept the actual button type
          variant={(actionConfig?.buttonOptions?.type as ButtonProps['variant']) ?? 'secondary'}
          data={actionConfig?.buttonOptions?.trackingData ?? {}}
          className={classNames('rc-EmailMeButton', actionConfig.buttonOptions?.rootClassName)}
          css={styles.mb}
          onClick={() => handleClick(v3EventKey)}
          component={<T extends TrackingProps>(props: T) =>
            isLink ? (
              <TrackedLink2 {...props} {...actionConfig.buttonOptions?.htmlAttributes} />
            ) : (
              <TrackedButton {...props} />
            )
          }
          fullWidth
        >
          {actionConfig?.buttonOptions?.label || _t('Get next steps')}
        </Button>
      );
    }
    default:
      return null;
  }
};

/**
 * This component renders a button corresponding to an action configuration for an Expression of Interest (EOI) flow.
 * The button behavior is configured based on the action type and can handle different types of actions such as requesting information, applying, and more.
 *
 * @param {Object} props.actionConfig - The configuration for the action associated with the button.
 * @param {string} props.trackingProductId - The ID of the product for tracking purposes.
 * @param {Object} props.expressionOfInterestText - Text content associated with the expression of interest.
 * @param {string} props.trackingNamePrefix - Prefix for the tracking name.
 * @param {string} props.personalizedMessageName - Name for personalized messages.
 * @param {number} [props.trackingIndex] - Optional index for tracking.
 * @param {Function} [props.setEoiTrackingIndex] - Optional function to set the EOI tracking index.
 * @param {boolean} props.isInEOI - Indicates whether the user is in the EOI flow.
 *
 * @returns {ReactElement | null} The rendered EoiButton component or null if the action type is not supported.
 */
export default memo(EoiButton);
